<template>
  <mf-card-container title="Configuração de limite dos disparos">
    <div class="dispatch-types-container">
      <mf-radio-button v-model="dispatchType" mandatory :options="dispatchOptions" :disabled="disabledDispatchSelection" />
    </div>
    <channel-limits
      v-if="hasChannel('sms')"
      ref="smsChannel"
      v-model="smsBudget"
      class="mb-12"
      channel="sms"
      :available-balance="smsAvailableBalance"
      :disabled="isMariaCampaignFinished"
    />
    <channel-limits
      v-if="hasChannel('whatsapp')"
      ref="whatsappChannel"
      v-model="whatsappBudget"
      class="mb-12"
      channel="whatsapp"
      :disabled="isMariaCampaignFinished"
    />
    <channel-limits v-if="hasChannel('push')" ref="pushChannel" v-model="pushBudget" class="mb-12" channel="push" :disabled="isMariaCampaignFinished" />
    <channel-limits
      v-if="hasChannel('email')"
      ref="emailChannel"
      v-model="emailBudget"
      class="mb-12"
      channel="email"
      :available-balance="emailAvailableBalance"
      :disabled="isMariaCampaignFinished"
    />
  </mf-card-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'

export default {
  name: 'DispatchesConfiguration',
  components: {
    ChannelLimits: () => import('./ChannelLimits.vue'),
  },
  mixins: [mariaCampaign],
  data: () => ({
    disabled: false,
    invalidChannels: new Set([]),
    channelBalance: {
      sms: 0,
      email: 0,
    },
  }),
  computed: {
    dispatchType: {
      get() {
        return this.campaign.messages_dispatcher.budget.limiter
      },
      set(value) {
        this.setOmniCampaignLimiter(value)
      },
    },
    smsBudget: {
      get() {
        return this.campaign.messages_dispatcher.budget.channels.sms
      },
      set(value) {
        this.setOmniCampaignBudget({ channel: 'sms', value })
      },
    },
    whatsappBudget: {
      get() {
        return this.campaign.messages_dispatcher.budget.channels.whatsapp
      },
      set(value) {
        this.setOmniCampaignBudget({ channel: 'whatsapp', value })
      },
    },
    pushBudget: {
      get() {
        return this.campaign.messages_dispatcher.budget.channels.push
      },
      set(value) {
        this.setOmniCampaignBudget({ channel: 'push', value })
      },
    },
    emailBudget: {
      get() {
        return this.campaign.messages_dispatcher.budget.channels.email
      },
      set(value) {
        this.setOmniCampaignBudget({ channel: 'email', value })
      },
    },
    smsAvailableBalance() {
      return this.channelBalance.sms
    },
    emailAvailableBalance() {
      return this.channelBalance.email
    },
    dispatchOptions() {
      return [
        {
          label: 'Limite geral',
          description: `Configure os limites máximos de disparo de cada canal de forma geral para a campanha. 
          Com essa opção, a Mar.ia faz uma divisão dos disparos através dos dias da campanha.`,
          value: 'CAMPAIGN',
        },
        {
          label: 'Limite por dia',
          description: `Configure os limites máximos de disparo de cada canal por dia. 
          Dessa maneira, você pode ter um maior controle da quantidade diária disparada.`,
          value: 'DAILY',
        },
      ]
    },
    disabledDispatchSelection() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign?.status)
    },
  },
  async mounted() {
    this.getInvalidChannels()
    const { dispatchesBalanceByChannel } = await this.getAvailableBalance()
    this.channelBalance = { ...dispatchesBalanceByChannel }
  },
  methods: {
    ...mapActions(['setOmniCampaignLimiter', 'setOmniCampaignBudget', 'getAvailableBalance']),
    hasChannel(channel) {
      return !this.invalidChannels.has(channel) && this.hasChannelConfigured(channel)
    },
    getInvalidChannels() {
      const channelNames = ['sms', 'email', 'whatsapp', 'push']
      this.invalidChannels = new Set(channelNames)

      try {
        const scheduleSettings = this.campaign?.messages_dispatcher?.settings?.schedule_settings

        if (!Array.isArray(scheduleSettings)) return

        scheduleSettings.forEach((schedule) => {
          if (!schedule || typeof schedule !== 'object') return

          const channels = schedule.channels
          if (!channels || typeof channels !== 'object') return

          Object.entries(channels).forEach(([channel, channelData]) => {
            if (!channelNames.includes(channel)) return
            if (!channelData || typeof channelData !== 'object') return

            const { active, message } = channelData

            if (channel === 'email') {
              if (message?.email_configuration_type === 'BUILDER') {
                if (active === true && Object.keys(message?.builder_email_configuration).length > 0) {
                  this.invalidChannels.delete(channel)
                }
              }
              if (message?.email_configuration_type === 'SIMPLE') {
                if (
                  active === true &&
                  typeof message?.simple_email_configuration?.text === 'string' &&
                  message?.simple_email_configuration?.text?.trim() !== ''
                ) {
                  this.invalidChannels.delete(channel)
                }
              }
              if (message?.email_configuration_type === 'HTML') {
                if (active === true && typeof message?.html_email_configuration?.text === 'string' && message?.html_email_configuration?.text?.trim() !== '') {
                  this.invalidChannels.delete(channel)
                }
              }
            } else if (channel === 'push') {
              if (
                (active === true && typeof message?.title === 'string' && message?.title?.trim() !== '') ||
                (typeof message?.text === 'string' && message?.text?.trim() !== '')
              ) {
                this.invalidChannels.delete(channel)
              }
            } else {
              if (active === true && typeof message?.text === 'string' && message?.text?.trim() !== '') {
                this.invalidChannels.delete(channel)
              }
            }
          })
        })
      } catch (error) {
        console.error(error.message)
      }
    },
    hasChannelConfigured(channel) {
      const { schedule_settings } = this.campaign?.messages_dispatcher?.settings
      if (schedule_settings.length <= 0) return false
      return schedule_settings.some((setting) => setting.channels?.[channel]?.active)
    },
    validateStepFiveFormData() {
      let isValidArray = []
      if (this.hasChannel('sms')) {
        isValidArray.push(this.$refs.smsChannel.validate())
      }

      if (this.hasChannel('whatsapp')) {
        isValidArray.push(this.$refs.whatsappChannel.validate())
      }

      if (this.hasChannel('push')) {
        isValidArray.push(this.$refs.pushChannel.validate())
      }

      if (this.hasChannel('email')) {
        isValidArray.push(this.$refs.emailChannel.validate())
      }

      const isValid = isValidArray.every((valid) => valid)
      if (!isValid) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente.', snackbarColor: 'error' })
      }
      return isValid
    },
  },
}
</script>

<style lang="scss" scoped>
.dispatch-types-container {
  width: 588px;
}
</style>
